import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { formatDate } from '@/lib/utils/date';
import { useCdrSearch } from '../api';
import type { CdrRow } from '../types';
import { dashIfEmpty } from '../utils';

export function CallDetailRecordsPage() {
  const userId = useAuthStore((s) => s.userId);
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());

  const [mobile, setMobile] = useState('');
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  const search = useCdrSearch();
  const rows = search.data?.response ?? [];
  const totalRecords = search.data?.totalRecords ?? 0;

  const handleSearch = () => {
    if (!mobile.trim()) {
      toast.error('Enter a mobile number to search');
      return;
    }
    if (startDate && endDate && startDate > endDate) {
      toast.error('Start date cannot be after end date');
      return;
    }
    search.mutate({
      bni: mobile.trim(),
      startDate: startDate ? formatDate(startDate) : '',
      endDate: endDate ? formatDate(endDate) : '',
      pageNo: 1,
      pageSize: 0,
      userType: isSubOrSuper ? 'sadmin' : 'user',
      ...(isSubOrSuper ? {} : { userId: String(userId) }),
    });
  };

  const columns = useMemo<ColumnDef<CdrRow>[]>(
    () => [
      {
        accessorKey: 'campId',
        header: 'Campaign',
        size: 110,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {dashIfEmpty(row.original.campId)}
          </span>
        ),
      },
      {
        accessorKey: 'cli',
        header: 'CLI',
        cell: ({ row }) => dashIfEmpty(row.original.cli),
      },
      {
        accessorKey: 'username',
        header: 'Username',
        cell: ({ row }) => dashIfEmpty(row.original.username),
      },
      {
        accessorKey: 'bni',
        header: 'Mobile',
        cell: ({ row }) => dashIfEmpty(row.original.bni),
      },
      {
        accessorKey: 'startTime',
        header: 'Start',
        size: 180,
        cell: ({ row }) => dashIfEmpty(row.original.startTime),
      },
      {
        accessorKey: 'answerTime',
        header: 'Answered',
        size: 180,
        cell: ({ row }) => dashIfEmpty(row.original.answerTime),
      },
      {
        accessorKey: 'endTime',
        header: 'Ended',
        size: 180,
        cell: ({ row }) => dashIfEmpty(row.original.endTime),
      },
      {
        accessorKey: 'answerStatus',
        header: 'Status',
        size: 130,
        cell: ({ row }) => dashIfEmpty(row.original.answerStatus),
      },
      {
        accessorKey: 'billingduration',
        header: 'Billing duration',
        size: 130,
        cell: ({ row }) => (
          <span className="tabular-nums">{dashIfEmpty(row.original.billingduration)}</span>
        ),
      },
    ],
    [],
  );

  const showInitialEmpty = !search.data && !search.isPending;
  const showNoMatchEmpty =
    Boolean(search.data) && rows.length === 0 && !search.isPending;

  return (
    <FillPage>
      <PageHeader
        title="Call Records"
        description="Look up individual call records by mobile number and date range."
      />

      <Card>
        <CardContent className="grid gap-3 p-4 md:grid-cols-4 md:items-end">
          <div className="space-y-1">
            <Label htmlFor="cdr-mobile">Mobile number *</Label>
            <Input
              id="cdr-mobile"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/[^\d+]/g, ''))}
              inputMode="tel"
              placeholder="Enter a phone number"
            />
          </div>
          <div className="space-y-1">
            <Label>From</Label>
            <DatePicker
              value={startDate ?? null}
              onChange={setStartDate}
              placeholder="Any date"
            />
          </div>
          <div className="space-y-1">
            <Label>To</Label>
            <DatePicker
              value={endDate ?? null}
              onChange={setEndDate}
              placeholder="Any date"
            />
          </div>
          <Button onClick={handleSearch} disabled={search.isPending} className="md:justify-self-end">
            {search.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Search
          </Button>
        </CardContent>
      </Card>

      <DataTable<CdrRow, unknown>
        tableId="reports:cdr:v1"
        columns={columns}
        data={rows}
        isLoading={search.isPending}
        searchPlaceholder="Filter results…"
        emptyTitle={
          showInitialEmpty
            ? 'Enter a mobile number to begin'
            : showNoMatchEmpty
              ? 'No records found'
              : 'No records'
        }
        emptyDescription={
          showInitialEmpty
            ? 'CDR lookups require a specific phone number.'
            : 'Try expanding the date range or check the number.'
        }
        getRowId={(row, idx) => row.id ?? `cdr-${idx}`}
        exportFilename={`cdr-${mobile || 'search'}.csv`}
        pageSize={10}
      />

      {totalRecords > 0 && (
        <div className="text-xs text-muted-foreground">
          Backend reports{' '}
          <span className="font-medium text-foreground">{totalRecords.toLocaleString()}</span>{' '}
          total records matching this search.
        </div>
      )}
    </FillPage>
  );
}
